import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Book, ArrowLeft, User, MessageSquare, ArrowRight, Headphones, BookOpen, Bookmark, Video } from 'lucide-react';
import { useBookmarks } from '../hooks/useBookmarks';
import {
  fetchChapterByNumber,
  fetchSlokasByChapter,
  getErrorMessage
} from '../api/client';
import ErrorMessage from './ErrorMessage';
import SlokaArtImage from './SlokaArtImage';
import { chapterImageUrl } from '../utils/mediaUrls';
import { SlokaRecitationAudio, MeaningRecitationAudio } from './VerseAudio';
import { useContinuousPlayer } from '../context/ContinuousPlayerContext';
import { DEFAULT_LANGUAGE } from '../utils/defaultLanguage';
import WordBreakdown from './WordBreakdown';

const ChapterDetail = () => {
  const { number } = useParams();
  const navigate = useNavigate();
  const { startPlayback } = useContinuousPlayer();
  const { isBookmarked, toggleBookmark } = useBookmarks();
  const [chapter, setChapter] = useState(null);
  const [slokas, setSlokas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('slokas');

  const loadChapterData = useCallback(async () => {
    if (!number) return;
    setLoading(true);
    setError(null);
    try {
      const chapterData = await fetchChapterByNumber(DEFAULT_LANGUAGE, parseInt(number, 10));
      setChapter(chapterData);

      const chapterSlokas = await fetchSlokasByChapter(
        chapterData.language,
        chapterData.chapterNumber
      );
      setSlokas(
        chapterSlokas.sort((a, b) => a.slokaNumber - b.slokaNumber)
      );
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [number]);

  useEffect(() => {
    loadChapterData();
  }, [loadChapterData]);

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="skeleton h-10 w-40 rounded-xl" />
        <div className="card overflow-hidden p-0">
          <div className="skeleton h-48 sm:h-64" style={{ borderRadius: 0 }} />
          <div className="p-6 space-y-5">
            <div className="flex gap-4 items-start">
              <div className="skeleton w-16 h-16 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="skeleton h-6 w-48" />
                <div className="skeleton h-4 w-24" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="skeleton h-12" />
              <div className="skeleton h-12" />
            </div>
            <div className="space-y-2">
              <div className="skeleton h-4 w-full" />
              <div className="skeleton h-4 w-4/5" />
              <div className="skeleton h-4 w-full" />
              <div className="skeleton h-4 w-3/4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={loadChapterData} />;
  }

  if (!chapter) {
    return (
      <div className="text-center py-12">
        <Book className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">Chapter not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center space-x-4">
        <Link to="/chapters" className="btn-secondary flex items-center">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Chapters
        </Link>
      </div>

      <div className="card overflow-hidden p-0">
        <img
          src={chapterImageUrl(chapter.chapterNumber)}
          alt={`Chapter ${chapter.chapterNumber}: ${chapter.chapterName}`}
            className="chapter-image-banner"
        />
        <div className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-16 h-16 bg-gita-saffron rounded-full flex items-center justify-center shrink-0">
              <span className="text-white font-bold text-xl">{chapter.chapterNumber}</span>
            </div>
            <div>
              <h1 className="chapter-title">{chapter.chapterName}</h1>
              <p className="text-gray-600">{chapter.verseCount} verses</p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap">
            <button
              type="button"
              onClick={() => navigate(`/read/${chapter.chapterNumber}`)}
              className="btn-secondary flex items-center flex-1 sm:flex-none justify-center"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Read
            </button>
            <button
              type="button"
              onClick={() => navigate(`/watch?chapter=${chapter.chapterNumber}&verse=1`)}
              className="btn-secondary flex items-center flex-1 sm:flex-none justify-center"
            >
              <Video className="w-4 h-4 mr-2" />
              Watch
            </button>
            <button
              type="button"
              onClick={() => {
                startPlayback({ startChapter: chapter.chapterNumber, endChapter: chapter.chapterNumber, variant: 'apple' });
                navigate('/listen');
              }}
              className="btn-primary flex items-center flex-1 sm:flex-none justify-center"
            >
              <Headphones className="w-4 h-4 mr-2" />
              Listen
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <h3 className="font-semibold text-gray-700 mb-1">Yoga Name</h3>
            <p className="text-gray-600">{chapter.yogaName}</p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-700 mb-1">Meaning</h3>
            <p className="text-gray-600">{chapter.meaning}</p>
          </div>
        </div>

        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('summary')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'summary'
                  ? 'border-gita-saffron text-gita-saffron'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <MessageSquare className="w-4 h-4 inline mr-2" />
              Summary
            </button>
            <button
              onClick={() => setActiveTab('slokas')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'slokas'
                  ? 'border-gita-saffron text-gita-saffron'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Book className="w-4 h-4 inline mr-2" />
              Slokas ({slokas.length})
            </button>
          </nav>
        </div>

        {activeTab === 'summary' && (
          <div className="prose max-w-none">
            <div className="bg-gita-cream p-6 rounded-xl border border-gita-gold/15">
              <h3 className="content-section-title">Chapter Summary</h3>
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {chapter.summary}
              </p>
            </div>
          </div>
        )}

        {activeTab === 'slokas' && (
          <div className="space-y-6">
            {slokas.map((sloka) => (
              <div key={sloka._id} className="sloka-verse-card border-l-4 border-gita-saffron py-4 pl-4 sm:pl-6">
                <div className="sloka-verse-grid">
                  <SlokaArtImage
                    chapterNumber={chapter.chapterNumber}
                    slokaNumber={sloka.slokaNumber}
                    alt={`Illustration for Chapter ${chapter.chapterNumber}, Verse ${sloka.slokaNumber}`}
                    className="sloka-verse-art"
                  />
                  <div className="sloka-verse-body min-w-0">
                <div className="flex items-center justify-between mb-3 gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-semibold text-gita-maroon shrink-0">
                      Verse {sloka.slokaNumber}
                    </span>
                    <span className="speaker-badge flex items-center">
                      <User className="w-3 h-3 mr-1" />
                      {sloka.speaker}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => toggleBookmark({
                        id: sloka._id,
                        chapterNumber: sloka.chapterNumber,
                        slokaNumber: sloka.slokaNumber,
                        sloka: sloka.sloka,
                        meaning: sloka.meaning
                      })}
                      className={`p-1.5 rounded-full transition-colors ${
                        isBookmarked(sloka._id) ? 'text-gita-gold' : 'text-gray-300 hover:text-gita-gold'
                      }`}
                      aria-label={isBookmarked(sloka._id) ? 'Remove bookmark' : 'Bookmark verse'}
                    >
                      <Bookmark className={`w-4 h-4 ${isBookmarked(sloka._id) ? 'fill-current' : ''}`} />
                    </button>
                    <Link
                      to={`/sloka/${sloka._id}`}
                      className="text-sm text-gita-saffron hover:text-gita-orange flex items-center whitespace-nowrap"
                    >
                      <span className="hidden sm:inline">View with commentary</span>
                      <ArrowRight className="ml-1 w-3 h-3" />
                    </Link>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="sloka-text bg-gray-50 p-4 rounded-lg">
                    {sloka.sloka}
                  </div>
                  <SlokaRecitationAudio
                    chapterNumber={chapter.chapterNumber}
                    slokaNumber={sloka.slokaNumber}
                    compact
                  />

                  <WordBreakdown
                    chapterNumber={sloka.chapterNumber}
                    slokaNumber={sloka.slokaNumber}
                    language={sloka.language || chapter.language || DEFAULT_LANGUAGE}
                  />

                  <div className="text-gray-700 bg-orange-50/60 p-4 rounded-lg">
                    <p className="font-medium text-gray-800 mb-1">Meaning</p>
                    <p>{sloka.meaning}</p>
                  </div>
                  <MeaningRecitationAudio
                    language={sloka.language || chapter.language}
                    chapterNumber={chapter.chapterNumber}
                    slokaNumber={sloka.slokaNumber}
                    compact
                  />
                </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default ChapterDetail;
